import { TAppDispatch } from 'store/types'
import slice from 'store/features/aiAvatar/slice'
import { AvatarSource } from 'store/types/aiAvatar'
import { IUserAudio } from 'store/types/audio'
import { uploadVoiceCloneFile_API } from 'api'

const { actions } = slice

// Uploads the voice file to the user-media endpoint (the backend stores it in
// My Files → Sounds) and attaches the uploaded sound as the cloned voice. On
// failure the status flips to 'failed', which opens the "Upload failed" popup
// with its "Try again" CTA. Creating a voice clone is free of charge (no
// credits check). API failures are already reported to Sentry by the
// api-factory, so the catch only drives the UI state.
const uploadVoiceCloneFile = (file: File) => async (dispatch: TAppDispatch) => {
  dispatch(actions.setVoiceCloneUploadStatus('uploading'))

  try {
    const formData = new FormData()
    formData.append('file', file)
    // Marks the upload as a voice sample so the backend stores it under
    // My Files → Sounds → Voice clone (the media library filters on it).
    formData.append('isVoiceSample', 'true')

    const { data } = await uploadVoiceCloneFile_API(formData)
    const [audio] = data.audios ?? []

    if (!audio) {
      throw new Error('Voice clone upload returned no audio')
    }

    dispatch(
      actions.setClonedVoice({
        id: audio.id,
        name: audio.title,
        url: audio.path,
        source: AvatarSource.UPLOAD
      })
    )
  } catch {
    dispatch(actions.setVoiceCloneUploadStatus('failed'))
  }
}

// Attaches an already-uploaded voice file picked from My Voices (the host
// media library). Sounds come back in the usersounds shape (`title`/`path`),
// not the visual-media shape (`fileName`/`filePath`).
const selectClonedVoiceFromMyFiles =
  (item: IUserAudio) => (dispatch: TAppDispatch) => {
    dispatch(
      actions.setClonedVoice({
        id: item.id,
        name: item.title,
        url: item.path,
        source: AvatarSource.MY_FILES
      })
    )
  }

// Detaches the cloned voice from the current avatar. The file itself is NOT
// deleted — it stays in My Files and can be re-picked from My Voices.
const removeClonedVoice = () => (dispatch: TAppDispatch) => {
  dispatch(actions.clearClonedVoice())
}

export const voiceCloneThunks = {
  uploadVoiceCloneFile,
  selectClonedVoiceFromMyFiles,
  removeClonedVoice
}
